import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, PiggyBank, Receipt, History, Radar, Info, Loader2, BadgeCheck, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiError } from "@/lib/api";
import { fmtEUR, fmtDateTime, SOURCE_LABELS, SALES_CHANNEL_LABELS } from "@/lib/constants";

const MarginRow = ({ label, value, hint, testId, big = false }) => (
  <div className="flex items-start justify-between gap-3 py-2" data-testid={testId}>
    <div className="min-w-0">
      <div className={big ? "text-sm font-medium" : "text-sm"}>{label}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
    {value == null ? (
      <span className="text-sm text-muted-foreground shrink-0">–</span>
    ) : (
      <span className={`font-display font-bold tabular-nums shrink-0 ${big ? "text-xl" : "text-base"} ${value < 0 ? "text-red-300" : "text-emerald-300"}`}>
        {fmtEUR(value)}
      </span>
    )}
  </div>
);

export default function MargeTab({ vehicle, reload }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [ekValue, setEkValue] = useState("");
  const [ekBusy, setEkBusy] = useState(false);
  const [costValues, setCostValues] = useState({});
  const [costsBusy, setCostsBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/vehicles/${vehicle.id}/margin`);
      setData(r.data);
      setEkValue(r.data.purchase_price != null ? String(r.data.purchase_price) : "");
      setCostValues(Object.fromEntries(Object.entries(r.data.costs || {}).map(([k, v]) => [k, v != null ? String(v) : ""])));
      setError(null);
    } catch (e) {
      setError(apiError(e, "Markt & Marge konnte nicht geladen werden"));
    }
  }, [vehicle.id]);

  useEffect(() => { load(); }, [load]);

  const saveEk = async () => {
    const p = ekValue === "" ? null : parseFloat(ekValue);
    if (p != null && (Number.isNaN(p) || p < 0)) {
      toast.error("Bitte einen gültigen Einkaufspreis eingeben.");
      return;
    }
    setEkBusy(true);
    try {
      await api.patch(`/vehicles/${vehicle.id}/purchase-price`, { purchase_price: p });
      toast.success(p != null ? `Einkaufspreis gespeichert: ${fmtEUR(p)}` : "Einkaufspreis entfernt");
      await load();
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setEkBusy(false); }
  };

  const saveCosts = async () => {
    const payload = {};
    for (const [k, v] of Object.entries(costValues)) {
      if (v !== "" && v != null) {
        const n = parseFloat(v);
        if (Number.isNaN(n) || n < 0) {
          toast.error("Bitte gültige Kostenbeträge eingeben.");
          return;
        }
        payload[k] = n;
      } else {
        payload[k] = 0;
      }
    }
    setCostsBusy(true);
    try {
      await api.patch(`/vehicles/${vehicle.id}/costs`, payload);
      toast.success("Kosten gespeichert");
      await load();
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setCostsBusy(false); }
  };

  if (error)
    return (
      <Card><CardContent className="py-10 text-center space-y-3">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={load} data-testid="marge-retry-button">Erneut versuchen</Button>
      </CardContent></Card>
    );

  if (!data)
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <div className="grid lg:grid-cols-2 gap-4"><Skeleton className="h-56" /><Skeleton className="h-56" /></div>
      </div>
    );

  const m = data.margins || {};
  const sold = data.sold_info;

  return (
    <div className="space-y-4" data-testid="marge-tab">
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="marge-visibility-hint">
        <Lock className="h-3.5 w-3.5 text-primary" />
        Interner Bereich – Einkaufspreis, Kosten und Margen sind nur für Inhaber &amp; Manager sichtbar und werden niemals veröffentlicht.
      </div>

      {/* Margen-Übersicht */}
      <Card data-testid="marge-margins-card">
        <CardHeader className="pb-1">
          <CardTitle className="text-base font-display flex items-center gap-2"><PiggyBank className="h-4 w-4 text-primary" /> Marge</CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <MarginRow
            label="Erwartete Marge vor nicht erfassten Kosten"
            hint={`Inseratspreis ${fmtEUR(data.current_listing_price)} − Einkaufspreis ${fmtEUR(data.purchase_price)}`}
            value={m.erwartete_marge_vor_nicht_erfassten_kosten}
            testId="marge-expected-before"
          />
          <MarginRow
            label="Erwartete Marge nach eingetragenen Kosten"
            hint={`abzüglich eingetragener Kosten (${fmtEUR(data.costs_total)})`}
            value={m.erwartete_marge_nach_eingetragenen_kosten}
            testId="marge-expected-after"
          />
          <MarginRow
            label="Realisierte Marge nach eingetragenen Kosten"
            hint={sold ? `Verkaufspreis ${fmtEUR(sold.sale_price)} − EK − Kosten` : "Wird nach dem Verkauf berechnet"}
            value={m.realisierte_marge_nach_eingetragenen_kosten}
            testId="marge-realized"
            big
          />
          {data.purchase_price == null && (
            <p className="text-xs text-amber-300 pt-2 flex items-center gap-1.5" data-testid="marge-no-ek-hint">
              <Info className="h-3.5 w-3.5 shrink-0" /> Einkaufspreis erfassen, um Margen zu berechnen.
            </p>
          )}
          <p className="text-[10px] text-muted-foreground pt-2">
            Hinweis: Steuern, Finanzierung und weitere betriebliche Kosten sind nicht enthalten. Negative Margen werden bewusst angezeigt.
          </p>
        </CardContent>
      </Card>

      {/* Verkaufs-Zusammenfassung */}
      {sold && (
        <Card className="border-emerald-500/30" data-testid="marge-sold-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-base font-display flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-emerald-400" /> Verkauf</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-x-6 text-sm">
            {[
              ["Einkaufspreis (intern)", fmtEUR(sold.purchase_price ?? data.purchase_price)],
              ["Erster Inseratspreis", fmtEUR(sold.first_listing_price ?? data.first_listing_price)],
              ["Letzter Inseratspreis", fmtEUR(sold.last_listing_price)],
              ["Tatsächlicher Verkaufspreis", fmtEUR(sold.sale_price)],
              ["Eingetragene Kosten", fmtEUR(sold.costs_total ?? data.costs_total)],
              ["Preisnachlass", sold.preisnachlass != null ? `${fmtEUR(sold.preisnachlass)}${sold.preisnachlass_pct != null ? ` (${sold.preisnachlass_pct}%)` : ""}` : "–"],
              ["Standtage", sold.days_in_stock != null ? `${sold.days_in_stock} Tage` : "–"],
              ["Verkaufskanal", SALES_CHANNEL_LABELS[sold.sale_channel] || sold.sale_channel || "–"],
              ["Verkaufsland / Zielmarkt", sold.sale_country || "–"],
              ["Verkaufsdatum", sold.sale_date || "–"],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between gap-3 py-1.5 border-b border-border/50">
                <span className="text-muted-foreground">{label}</span>
                <span className="tabular-nums text-right">{val}</span>
              </div>
            ))}
            <div className="sm:col-span-2">
              <MarginRow label="Realisierte Marge nach eingetragenen Kosten" value={sold.realisierte_marge_nach_eingetragenen_kosten} testId="marge-sold-realized" big />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Einkaufspreis */}
        <Card data-testid="marge-ek-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-base font-display flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Einkaufspreis – intern</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <div className="flex gap-2">
              <Input type="number" min="0" value={ekValue} onChange={(e) => setEkValue(e.target.value)}
                placeholder="z.B. 21500" className="h-11" data-testid="marge-ek-input" />
              <Button onClick={saveEk} disabled={ekBusy} className="h-11 shrink-0" data-testid="marge-ek-save">
                {ekBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Wird niemals veröffentlicht und dient ausschließlich der Margen- und späteren DealAlert-Analyse.
            </p>
          </CardContent>
        </Card>

        {/* Kosten */}
        <Card data-testid="marge-costs-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-base font-display flex items-center gap-2"><Receipt className="h-4 w-4 text-primary" /> Interne Kosten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {Object.keys(data.costs || {}).map((key) => (
              <div key={key} className="flex items-center gap-2">
                <Label className="w-40 shrink-0 text-xs text-muted-foreground">{data.cost_labels?.[key] || key}</Label>
                <Input type="number" min="0" value={costValues[key] ?? ""} placeholder="0"
                  onChange={(e) => setCostValues((c) => ({ ...c, [key]: e.target.value }))}
                  className="h-9" data-testid={`marge-cost-${key}`} />
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-muted-foreground">Summe: <span className="tabular-nums font-medium text-foreground">{fmtEUR(data.costs_total)}</span></span>
              <Button size="sm" variant="outline" onClick={saveCosts} disabled={costsBusy} data-testid="marge-costs-save">
                {costsBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Kosten speichern"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Preisverlauf */}
        <Card data-testid="marge-price-history-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-base font-display flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Preisverlauf</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            <div className="flex items-center justify-between text-sm pb-1.5 border-b border-border/60">
              <span className="text-muted-foreground flex items-center gap-1.5">
                Erster Inseratspreis
                <span className="rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-300 text-[9px] px-1.5 py-0.5">unveränderlich</span>
              </span>
              <span className="tabular-nums font-medium" data-testid="marge-first-listing-price">{fmtEUR(data.first_listing_price)}</span>
            </div>
            {(data.price_history || []).length === 0 && (
              <p className="text-sm text-muted-foreground py-3 text-center">Noch keine Preisänderungen erfasst.</p>
            )}
            {[...(data.price_history || [])].reverse().map((p, i) => (
              <div key={p.id || i} className="flex items-center justify-between text-sm" data-testid="marge-price-history-row">
                <span className="tabular-nums font-medium">
                  {fmtEUR(p.price)}
                  {p.previous_price != null && (
                    <span className={`ml-2 text-[10px] ${p.price < p.previous_price ? "text-amber-300" : "text-emerald-300"}`}>
                      {p.price < p.previous_price ? "▼" : "▲"} vorher {fmtEUR(p.previous_price)}
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">{SOURCE_LABELS[p.source] || p.source} · {fmtDateTime(p.changed_at)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* DealAlert Status */}
        <Card data-testid="marge-dealalert-card">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base font-display flex items-center gap-2"><Radar className="h-4 w-4 text-primary" /> DealAlert Market Intelligence</CardTitle>
              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[10px] font-semibold px-2 py-0.5" data-testid="marge-dealalert-status">
                Noch nicht verbunden
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Die Anbindung an DealAlert (echte Marktpreise, Vergleichsfahrzeuge, Preisposition) wird gemeinsam mit dem
              Pilotpartner eingerichtet. Bis dahin werden bewusst <b>keine simulierten Marktdaten</b> angezeigt.
            </p>
            <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2" data-testid="marge-outbox-box">
              <div className="text-xs font-medium flex items-center gap-1.5">
                <TrendingDown className="h-3.5 w-3.5 text-primary rotate-180" />
                {(data.outbox_events || []).length} Ereignis{(data.outbox_events || []).length === 1 ? "" : "se"} für die spätere Synchronisation vorgemerkt
              </div>
              {(data.outbox_events || []).slice(0, 6).map((e) => (
                <div key={e.id} className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid="marge-outbox-event">
                  <span>{data.event_labels?.[e.event_type] || e.event_type}</span>
                  <span>{fmtDateTime(e.created_at)}</span>
                </div>
              ))}
              {(data.outbox_events || []).length === 0 && (
                <p className="text-[11px] text-muted-foreground">Sobald Einkauf, Preisänderungen oder Verkäufe erfasst werden, erscheinen sie hier.</p>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Ereignisse werden idempotent, versioniert und ohne personenbezogene Daten gespeichert.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
